import React, { useCallback } from 'react';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import CustomChip from '../../../Approvals/Component/CreateApprovalForm/CustomChip';
import ControlledTextField from '../../../RiverusUI/Components/ControlledTextField';
import CustomModal from '../../../RiverusUI/Components/CustomModal';
import { editReminder } from '../../../Services/DocumentLibrary';

interface ReminderConfirmationModalProps {
  open: boolean;
  handleClose: VoidFunction;
  onClose?: VoidFunction;
  users?: string;
  approvalData?: any;
  propsData?: any;
  draftItemData?: any;
  getUserDetail?: any;
  name?: string;
  reminderData?: any;
  reminderId?: any;
  handleCloseDialog?: VoidFunction;
  dialogReminderData?: any;
  approverReminderId?: any;
  reminderById?: any;
}

const ReminderConfirmationModal: React.FC<ReminderConfirmationModalProps> = ({
  open,
  handleClose,
  onClose,
  users,
  approvalData,
  propsData,
  draftItemData,
  getUserDetail,
  name,
  reminderData,
  reminderId,
  handleCloseDialog,
  dialogReminderData,
  approverReminderId,
  reminderById,
}) => {
  const { handleSubmit, control } = useForm();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: update_reminder, isPending: loadingReminder } = useMutation({
    mutationKey: ['update_reminder'],
    mutationFn: editReminder,
    onSuccess: () => {
      enqueueSnackbar('Reminder updated successfully!', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      queryClient.invalidateQueries({
        queryKey: ['get_reminders'],
      });
      handleClose();
      onClose?.();
      handleCloseDialog?.();
    },
    onError: () => {
      enqueueSnackbar('Failed to update Reminder!', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });

  const sendReminder = useCallback(
    (data: any) => {
      const reminderPayload = {
        send_reminder: true,
        comments: data?.note,
      };

      const sendNowReminderPayload = {
        id:
          dialogReminderData?.id ||
          approverReminderId ||
          reminderData?.id ||
          reminderId ||
          propsData?.reminder?.id ||
          reminderData?.reminder_id,
        body: reminderPayload,
      };

      update_reminder(sendNowReminderPayload);
    },
    [
      propsData,
      reminderData,
      update_reminder,
      reminderId,
      dialogReminderData,
      approverReminderId,
    ]
  );

  const onSubmit = (data: any) => {
    sendReminder({ ...data, users });
  };

  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      title="Send a reminder"
      maxWidth="md"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} pt="20px" sx={{ width: '500px' }}>
          <Typography variant="subtitle2" color="#000">
            Are you sure you want to send a reminder to the following:
          </Typography>

          <Stack
            direction="row"
            justify-content="start"
            align-item="center"
            spacing={1}
          >
            {dialogReminderData?.users?.length > 0 ? (
              dialogReminderData.users.map((user: any) => (
                <CustomChip
                  icon={<PersonOutlineIcon />}
                  key={user?.id}
                  label={user?.first_name}
                  sx={{
                    background: !user?.is_active
                      ? 'rgba(0, 0, 0, 0.15)'
                      : '#FFECF1',
                    padding: '15px 10px',
                    color: !user?.is_active ? '' : '#6D264C',
                    borderRadius: '5px',
                  }}
                />
              ))
            ) : reminderById?.users?.length > 0 ? (
              reminderById?.users?.map((user: any) => (
                <CustomChip
                  icon={<PersonOutlineIcon />}
                  key={user?.id}
                  label={user?.first_name}
                  sx={{
                    background: !user?.is_active
                      ? 'rgba(0, 0, 0, 0.15)'
                      : '#FFECF1',
                    padding: '15px 10px',
                    color: !user?.is_active ? '' : '#6D264C',
                    borderRadius: '5px',
                  }}
                />
              ))
            ) : reminderData?.users?.length > 0 ? (
              reminderData?.users?.map((user: any) => (
                <CustomChip
                  icon={<PersonOutlineIcon />}
                  key={user?.id}
                  label={user?.first_name}
                  sx={{
                    background: !user?.is_active
                      ? 'rgba(0, 0, 0, 0.15)'
                      : '#FFECF1',
                    padding: '15px 10px',
                    color: !user?.is_active ? '' : '#6D264C',
                    borderRadius: '5px',
                  }}
                />
              ))
            ) : reminderData?.external_user?.length > 0 ? (
              reminderData?.external_user?.map((user: any) => (
                <CustomChip
                  icon={<PersonOutlineIcon />}
                  key={user?.id}
                  label={user?.name}
                  sx={{
                    background: !user?.is_active
                      ? 'rgba(0, 0, 0, 0.15)'
                      : '#FFECF1',
                    padding: '15px 10px',
                    color: !user?.is_active ? '' : '#6D264C',
                    borderRadius: '5px',
                  }}
                />
              ))
            ) : (
              <CustomChip
                icon={
                  <PersonOutlineIcon
                    sx={{ fill: '#88305F', fontSize: '14px' }}
                  />
                }
                label={
                  name ||
                  approvalData?.approvers?.first_name ||
                  getUserDetail?.first_name ||
                  draftItemData?.user_name ||
                  propsData?.user_name
                }
                sx={{
                  background: '#FFF7FA',
                  borderRadius: '8px',
                }}
              />
            )}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight="500">
              (Optional) Note for approver:
            </Typography>
            <ControlledTextField
              name="note"
              control={control}
              label="Note"
              fullWidth
            />
          </Stack>
          <Stack alignItems="center" direction="row" marginTop="20px">
            <LoadingButton
              loading={loadingReminder}
              variant="contained"
              type="submit"
            >
              Yes, send reminder
            </LoadingButton>
            <Button variant="outlined" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </Stack>
      </form>
    </CustomModal>
  );
};

export default ReminderConfirmationModal;
